import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Building2, Zap, Shield, CheckCircle2, ArrowRight } from "lucide-react";

const INDUSTRIES = ["Manufacturing","Energy & Utilities","Transportation & Logistics","Technology","Retail","Construction","Agriculture","Finance","Aviation","Shipping","Chemicals","Oil & Gas","Cement","Steel","Data Center","Other"];
const COUNTRIES = ["United States","United Kingdom","Germany","France","Netherlands","Spain","Italy","Sweden","Norway","Switzerland","Ireland","Canada","Australia","New Zealand","Japan","Singapore","Hong Kong","South Korea","India","United Arab Emirates","Saudi Arabia","Brazil","Mexico","Argentina","South Africa","Kenya","Nigeria","Egypt","Turkey","Poland","Denmark","Finland","Belgium","Austria","Portugal","Chile","Colombia","Indonesia","Thailand","Vietnam","Malaysia","Philippines","Other"];
const TIMELINES = ["Immediate (< 1 month)","This quarter","Within 6 months","Within a year","Long-term / ongoing"];
const CATEGORIES = ["forestry","renewable","ocean","agriculture","direct_air"];

const empty = { company_name:"", contact_name:"", email:"", phone:"", country:"", industry:"", tons_needed:10000, timeline:"", preferred_categories:[], notes:"" };

export default function Enterprise() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const toggleCat = (c) => {
    const cur = form.preferred_categories || [];
    setForm({ ...form, preferred_categories: cur.includes(c) ? cur.filter(x=>x!==c) : [...cur, c] });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, tons_needed: Number(form.tons_needed) };
      const { data } = await api.post("/enterprise/quote", payload);
      setDone(data);
      toast.success("Quote request received — we'll be in touch within 24 hours");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <CheckCircle2 className="w-16 h-16 text-[#2C5530] mx-auto"/>
        <h1 className="mt-6 text-4xl tracking-tighter font-light">Quote request received</h1>
        <p className="mt-3 text-stone-600">
          Reference: <span className="font-mono text-sm bg-stone-100 px-2 py-1 rounded">{done.quote_id.slice(0,8).toUpperCase()}</span>
        </p>
        <p className="text-stone-600 mt-2">Our enterprise team will reach out within 24 hours with a bespoke portfolio and volume pricing.</p>
        <div className="mt-8 flex gap-3 justify-center">
          <Link to="/marketplace"><Button variant="outline" className="rounded-full h-11 px-6">Explore projects</Button></Link>
          <Link to="/"><Button className="bg-[#2C5530] hover:bg-[#1F3D22] text-white rounded-full h-11 px-6">Back to home</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D97757]">Enterprise · 10,000+ tons</div>
          <h1 className="mt-3 text-4xl sm:text-5xl tracking-tighter font-light leading-[1.05]">
            Retire millions of tons in one deal.
          </h1>
          <p className="mt-6 text-lg text-stone-600 max-w-lg">
            For heavy industry, aviation, shipping and data centers that need to secure long-term supply of high-integrity credits at volume pricing.
          </p>
          <div className="mt-10 space-y-5">
            {[
              { icon: Zap, t: "Volume pricing", d: "Bespoke rates from 8% off market for orders over 10,000 tons." },
              { icon: Shield, t: "Multi-project portfolios", d: "Diversify risk across forestry, renewable, ocean & DAC — with permanence guarantees." },
              { icon: Building2, t: "Direct developer access", d: "Talk to project developers, visit sites, receive quarterly monitoring reports." },
            ].map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.t} className="flex gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#2C5530]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-[#2C5530]"/>
                  </div>
                  <div>
                    <div className="font-medium">{f.t}</div>
                    <div className="text-sm text-stone-600 mt-0.5">{f.d}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-10 p-5 rounded-2xl bg-stone-100 border border-stone-200">
            <div className="text-xs uppercase tracking-widest text-stone-500">Trusted by</div>
            <div className="mt-2 text-sm">Fortune 500 manufacturers, top 10 global airlines, hyperscale cloud providers</div>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white border border-stone-200 rounded-3xl p-8 space-y-4 h-fit lg:sticky lg:top-24">
          <h2 className="text-2xl tracking-tight font-medium mb-2">Request a custom quote</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Company name</Label>
              <Input required value={form.company_name} onChange={set("company_name")} className="mt-1 bg-stone-50 border-stone-200 h-11" data-testid="ent-company"/>
            </div>
            <div>
              <Label>Contact name</Label>
              <Input required value={form.contact_name} onChange={set("contact_name")} className="mt-1 bg-stone-50 border-stone-200 h-11" data-testid="ent-contact"/>
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" required value={form.email} onChange={set("email")} className="mt-1 bg-stone-50 border-stone-200 h-11" data-testid="ent-email"/>
            </div>
            <div>
              <Label>Phone (optional)</Label>
              <Input value={form.phone} onChange={set("phone")} className="mt-1 bg-stone-50 border-stone-200 h-11"/>
            </div>
            <div>
              <Label>Country</Label>
              <Select value={form.country} onValueChange={set("country")}>
                <SelectTrigger className="mt-1 bg-stone-50 border-stone-200 h-11" data-testid="ent-country"><SelectValue placeholder="Select country"/></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Industry</Label>
              <Select value={form.industry} onValueChange={set("industry")}>
                <SelectTrigger className="mt-1 bg-stone-50 border-stone-200 h-11"><SelectValue placeholder="Select industry"/></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  {INDUSTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tons needed / year</Label>
              <Input type="number" min={10000} required value={form.tons_needed} onChange={set("tons_needed")} className="mt-1 bg-stone-50 border-stone-200 h-11" data-testid="ent-tons"/>
            </div>
            <div className="col-span-2">
              <Label>Preferred timeline</Label>
              <Select value={form.timeline} onValueChange={set("timeline")}>
                <SelectTrigger className="mt-1 bg-stone-50 border-stone-200 h-11"><SelectValue placeholder="Select timeline"/></SelectTrigger>
                <SelectContent className="bg-white">
                  {TIMELINES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Preferred project categories</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <label key={c} className={`px-3 py-2 rounded-full border cursor-pointer text-sm capitalize transition-colors ${form.preferred_categories.includes(c) ? "bg-[#2C5530] text-white border-[#2C5530]" : "bg-white border-stone-200"}`}>
                    <Checkbox checked={form.preferred_categories.includes(c)} onCheckedChange={() => toggleCat(c)} className="hidden"/>
                    {c.replace("_"," ")}
                  </label>
                ))}
              </div>
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={set("notes")} rows={3} className="mt-1 bg-stone-50 border-stone-200" placeholder="Regions of interest, budget, board deadlines…"/>
            </div>
          </div>
          <Button type="submit" disabled={loading} data-testid="ent-submit" className="w-full h-12 bg-[#2C5530] hover:bg-[#1F3D22] text-white rounded-full mt-4">
            {loading ? "Submitting…" : <>Request quote <ArrowRight className="ml-2 w-4 h-4"/></>}
          </Button>
          <div className="text-xs text-stone-500 text-center">We respond within 24 business hours.</div>
        </form>
      </div>
    </div>
  );
}
